import { useEffect, useState } from "react";
import api from "@/lib/api";
import { X, Truck, Send } from "lucide-react";
import { toast } from "sonner";

const REASONS = [
  "New floor stock", "Missing POS material", "Damaged POS material",
  "Incorrect size", "Incorrect model", "Promotion change",
  "Replacement required", "Additional display units", "Other",
];

export default function POSRequestModal({ customerId, onClose, onCreated }) {
  const [req, setReq] = useState(null);
  const [reason, setReason] = useState("Missing POS material");
  const [priority, setPriority] = useState("Normal");
  const [notes, setNotes] = useState("");
  const [selected, setSelected] = useState({}); // idx -> qty override

  useEffect(() => {
    api.get(`/customers/${customerId}/pos-requirements`).then(r => {
      setReq(r.data);
      const init = {};
      (r.data.items || []).forEach((it, i) => { init[i] = it.quantity_needed; });
      setSelected(init);
    });
  }, [customerId]);

  const submit = async (status) => {
    if (!req) return;
    const items = (req.items || [])
      .map((it, i) => ({ ...it, quantity_needed: Number(selected[i] || 0) }))
      .filter(it => it.quantity_needed > 0);
    if (items.length === 0) return toast.error("Adjust at least one item quantity");
    try {
      await api.post("/pos-requests", { customer_id: customerId, items, reason, priority, notes, status });
      toast.success(`POS request ${status.toLowerCase()}`);
      onCreated && onCreated(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!req) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="pos-request-modal">
        <div className="p-5 border-b flex justify-between items-start">
          <div>
            <h3 className="text-xl font-heading font-semibold flex items-center gap-2"><Truck className="w-5 h-5 text-[#003DA5]" /> Request POS Materials</h3>
            <div className="text-xs text-slate-500 mt-1">{req.customer_name} • auto-computed from floor units currently displayed</div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {req.items.length === 0 ? (
            <div className="text-sm text-slate-500 text-center py-8">No POS materials required — no floor units are currently displayed. Add ✓ floor units first, then request POS.</div>
          ) : (
            <>
              <div>
                <div className="text-xs uppercase font-semibold text-slate-500 mb-2">Required materials ({req.items.length})</div>
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F3F7FC] text-xs uppercase text-slate-500">
                      <tr>
                        <th className="text-left px-3 py-2">Material</th>
                        <th className="text-left px-3 py-2">For</th>
                        <th className="text-left px-3 py-2">Required</th>
                        <th className="text-left px-3 py-2">Warehouse</th>
                        <th className="text-left px-3 py-2">Qty needed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {req.items.map((it, i) => (
                        <tr key={i} className="border-t" data-testid={`req-item-${i}`}>
                          <td className="px-3 py-2 font-medium">{it.material_name}
                            {it.mandatory && <span className="ml-1 text-[10px] text-red-600 font-bold">MANDATORY</span>}
                          </td>
                          <td className="px-3 py-2 text-xs text-slate-600">{it.model_name}{it.size ? ` — ${it.size}` : ""}</td>
                          <td className="px-3 py-2">{it.quantity_required}</td>
                          <td className="px-3 py-2 text-xs">{it.warehouse_stock}</td>
                          <td className="px-3 py-2">
                            <input type="number" min="0" value={selected[i] ?? it.quantity_needed}
                              onChange={e => setSelected({ ...selected, [i]: Number(e.target.value) })}
                              className="w-20 h-9 px-2 border rounded" data-testid={`req-qty-${i}`} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase font-semibold text-slate-500">Reason</label>
                  <select value={reason} onChange={e => setReason(e.target.value)} className="mt-1 w-full h-11 px-3 border rounded-lg" data-testid="req-reason">
                    {REASONS.map(r => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase font-semibold text-slate-500">Priority</label>
                  <select value={priority} onChange={e => setPriority(e.target.value)} className="mt-1 w-full h-11 px-3 border rounded-lg" data-testid="req-priority">
                    <option>Low</option><option>Normal</option><option>High</option><option>Urgent</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase font-semibold text-slate-500">Notes</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="mt-1 w-full p-3 border rounded-lg" data-testid="req-notes" />
              </div>
            </>
          )}
        </div>
        <div className="p-5 border-t flex justify-end gap-2">
          <button onClick={onClose} className="h-11 px-5 border rounded-lg">Cancel</button>
          {req.items.length > 0 && (
            <>
              <button onClick={() => submit("Draft")} className="h-11 px-5 border rounded-lg font-semibold" data-testid="req-save-draft">Save draft</button>
              <button onClick={() => submit("Submitted")} className="mlily-btn h-11 px-5 rounded-lg font-semibold flex items-center gap-2" data-testid="req-submit"><Send className="w-4 h-4" /> Submit request</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
